import {ApolloProvider} from '@apollo/client';
import {Suspense, useEffect, useMemo, useRef} from 'react';
import {Outlet, useParams} from 'react-router-dom';
import {AuthProvider, useAuth} from '~/lib/auth';
import type {TokenStore, PreviewToken} from '~/lib/auth';
import {createApolloClient} from '~/lib/graphql/apollo-client';
import {ErrorScreen} from '~/components/error-screen';
import {LoadingScreen} from '~/components/loading-screen';
import {VerificationOverlay} from '~/sections/verification';

function usePreviewToken(): PreviewToken | null {
  const ref = useRef<PreviewToken | null | undefined>(undefined);

  if (ref.current === undefined) {
    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);
    const token = params.get('preview-token');
    const expiresAt = params.get('preview-token-expiry');

    if (token && expiresAt) {
      ref.current = {token, expiresAt};
    } else {
      ref.current = null;
    }
  }

  // Clear token from URL in an effect to avoid triggering React Router remount
  useEffect(() => {
    if (ref.current) {
      window.history.replaceState(
        {},
        '',
        window.location.pathname + window.location.search,
      );
    }
  }, []);

  return ref.current;
}

export const Layout = () => {
  const {identifier} = useParams<{identifier: string}>();
  const previewToken = usePreviewToken();

  return (
    <AuthProvider
      key={identifier}
      identifier={identifier!}
      previewToken={previewToken}
    >
      <AuthGate />
    </AuthProvider>
  );
};

const AuthGate = () => {
  const {authState, identifier} = useAuth();

  switch (authState.status) {
    case 'loading':
      return <LoadingScreen />;
    case 'verification_required':
      return <VerificationOverlay organization={authState.organization} />;
    case 'not_found':
      return (
        <ErrorScreen
          title="Not found"
          message="The content you're looking for doesn't exist or the link is invalid."
        />
      );
    case 'preview_expired':
      return (
        <ErrorScreen
          title="Preview expired"
          message="This preview link has expired. Generate a new preview from the page editor."
        />
      );
    case 'rate_limited':
      return (
        <ErrorScreen
          title="This page is busy right now"
          message="A lot of people are opening it at the moment. Please try again in a few seconds."
          showRefresh
        />
      );
    case 'error':
      return (
        <ErrorScreen
          title="Something went wrong"
          message="An unexpected error occurred. Please try again later."
        />
      );
    case 'authenticated':
      return (
        <AuthenticatedLayout
          identifier={identifier}
          tokenStore={authState.tokenStore}
        />
      );
  }
};

interface AuthenticatedLayoutProps {
  identifier: string;
  tokenStore: TokenStore;
}

const AuthenticatedLayout = ({
  identifier,
  tokenStore,
}: AuthenticatedLayoutProps) => {
  const {reauthenticate} = useAuth();

  const apolloClient = useMemo(
    () =>
      createApolloClient(identifier, tokenStore, {
        onAccessDenied: reauthenticate,
      }),
    [identifier, tokenStore, reauthenticate],
  );

  return (
    <ApolloProvider client={apolloClient}>
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    </ApolloProvider>
  );
};
